import _ from 'lodash';
import { denormalizeUrl } from '../../libs/utils';

import Oidc from 'oidc-client';

export default class OAuth {
	user: any;
	options: any;
	manager: any;

	constructor(options = {}) {
		this.user = null;

		this.options = _.merge(
			{
				client: null,
				authority: null,

				notificationElementClassName: 'app',

				oidc: {}
			},
			options
		);

		this.options.oidc = _.merge(
			{
				authority: this.options.authority,
				client_id: this.options.client,

				redirect_uri: denormalizeUrl(this.root).toLowerCase(),
				post_logout_redirect_uri: denormalizeUrl(
					this.root
				).toLowerCase(),

				response_type: 'code',
				scope: 'openid profile workzone offline_access',

				monitorSession: false
			},
			this.options.oidc
		);
	}

	get host() {
		return [window.location.protocol, window.location.host].join('//');
	}

	get root() {
		return [
			window.location.protocol,
			[
				window.location.host,
				window.location.pathname.substr(
					1,
					window.location.pathname.lastIndexOf('/')
				)
			]
				.filter(Boolean)
				.join('/')
		].join('//');
	}

	get isLoggedIn() {
		return !!this.user;
	}

	static normalize(path: string) {
		return path
			.split('://')
			.map(p => p.split('/').filter(Boolean).join('/'))
			.join('://');
	}

	static denormalize(path: string) {
		return path && path.substr(-1) !== '/' ? `${path}/` : path;
	}

	setUser(user: any) {
		this.user = user;
	}

	getAuthorizationHeader() {
		return this.getToken().then((response: any) =>
			this.buildAuthorizationHeader(response)
		);
	}

	buildAuthorizationHeader(token: any) {
		return {
			Authorization: [token?.token_type, token?.access_token]
				.filter(Boolean)
				.join(' ')
		};
	}

	getToken() {
		if (this.isLoggedIn && !this.user.expired) {
			return Promise.resolve(this.user);
		} else {
			return this.manager
				.signinSilent()
				.then((user: any) => {
					this.setUser(user);

					return this.user;
				})
				.catch(() => this.relogin());
		}
	}

	getAccessToken() {
		return this.getToken().then((token: any) => token?.access_token);
	}

	init() {
		this.manager = new Oidc.UserManager(this.options.oidc);

		return this.manager
			.getUser()
			.then((user: any) => {
				if (user) {
					if (user.expired) {
						this.setUser(null);

						this.manager.removeUser();
					} else {
						//this.manager.clearStaleState();

						this.setUser(user);

						this.restoreLocation();
					}
				}

				if (!this.isLoggedIn) {
					return this.relogin();
				}
			})
			.catch((e: any) => {
				this.restoreLocation();

				if (e.message === 'invalid_client') {
					return this.notifyLoginFailed(e.message);
				}

				return this.manager.signinRedirect();
			});
	}

	login() {
		if (this.manager) {
			if (this.isAuthorized()) {
				return this.manager
					.signinCallback()
					.then((user: any) => {
						this.setUser(user);

						this.restoreLocation();
					})
					.catch((e: any) => this.notifyLoginFailed(e));
			} else {
				const state = { location: window.location.href };

				return this.manager.signinRedirect({
					state,
					useReplaceToNavigate: true
				});
			}
		}
	}

	relogin() {
		this.setUser(null);

		return this.manager?.clearStaleState().then(() => this.login());
	}

	logout() {
		return this.manager
			?.clearStaleState()
			.then(() => this.manager.signoutRedirect());
	}

	restoreLocation() {
		let location = this.root; // replacement should default to app host, not endpoint host

		if (
			this.options.oidc.response_type === 'code' &&
			window.location.hash
		) {
			// we have route, and we must return to it back
			location = [OAuth.normalize(location), window.location.hash].join(
				'/'
			);
		}

		if (this.user?.state?.location) {
			location = this.user.state.location;
		}

		try {
			// return back to original location
			window.history.replaceState({}, window.document.title, location);
		} catch {
			/* fail silently */
		}
	}

	isAuthorized() {
		if (this.options.oidc.response_type === 'code') {
			const authResponse = OAuth.readQuery();

			return !!authResponse.code;
		} else {
			const authResponse = OAuth.readHash();

			return !!(authResponse.id_token && authResponse.access_token);
		}
	}

	notifyLoginFailed(e: any = null) {
		return this.notify({
			message: [
				'You do not have permission to view this page using the credentials that you supplied',
				e
			]
				.filter(Boolean)
				.join('<br>')
		});
	}

	notify(e: any) {
		const elm = document.getElementsByClassName(
			this.options.notificationElementClassName
		);

		if (elm.length) {
			elm[0].outerHTML = `<div class="ui-critical-error">${e.message}</div>`;
		}
	}

	static readHash() {
		return OAuth.readLocationProperty(window.location.hash);
	}

	static readQuery() {
		return OAuth.readLocationProperty(window.location.search);
	}

	static readLocationProperty(property: any) {
		if (property) {
			const query = property.substr(1);

			return query.split('&').reduce((acc: any, item: any) => {
				const parts = item.split('=');

				acc[parts[0]] = parts[1];

				return acc;
			}, {});
		}

		return {};
	}
}
